import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Crown, Sparkles, Camera, Briefcase, GraduationCap, Cpu, Percent, Gem, Megaphone, Lock } from "lucide-react";
import { toast } from "sonner";
import CommunityLayout from "@/components/community/CommunityLayout";
import { BulletinCard, EventCard, EmptyState } from "@/components/community/Cards";
import { cApi, errMsg, useCommunityMe, STOCK, SET_REQUIREMENT, PRIVE_SHOP_URL } from "@/lib/community";
import { startGoogleLogin } from "@/context/CustomerAuthContext";

const BENEFITS = [
  [Megaphone, "Exclusive member announcements"],
  [Sparkles, "Early access to product releases and restocks"],
  [Camera, "Photoshoot opportunities"],
  [Briefcase, "One free business headshot opportunity"],
  [Gem, "Exclusive activities and get-togethers"],
  [GraduationCap, "Business education"],
  [Cpu, "AI and website-building education"],
  [Percent, "Discounts on professional photoshoots"],
  [Crown, "Exclusive community opportunities"],
];

function RequestForm({ onDone }) {
  const [f, setF] = useState({ order_reference: "", note: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await cApi.post("/community/prive/request", f);
      toast.success("Verification requested — we'll email you once approved");
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-6" data-testid="prive-request-form">
      <p className="text-sm text-[#4A403B] leading-relaxed">
        Purchased a qualifying Jenova set with this email? Request verification and our founder will confirm your order.
      </p>
      <input className="jc-input" placeholder="Order number (optional)" value={f.order_reference} onChange={(e) => setF({ ...f, order_reference: e.target.value })} data-testid="prive-order-input" />
      <input className="jc-input" placeholder="Anything we should know? (optional)" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} data-testid="prive-note-input" />
      <button disabled={busy} className="jc-btn jc-btn-primary" data-testid="prive-request-btn">{busy ? "Sending…" : "Request Privé verification"}</button>
    </form>
  );
}

function Gate() {
  const { user, me, loading, reload } = useCommunityMe();
  if (loading) return null;
  const s = me?.membership?.prive_status || "none";
  let body;
  if (!user) body = (
    <>
      <p className="text-sm text-[#4A403B]">Sign in with the email you used at checkout to verify your purchase.</p>
      <button onClick={() => startGoogleLogin("/prive")} className="jc-btn jc-btn-primary mt-6" data-testid="prive-signin-btn">Member sign in</button>
    </>
  );
  else if (s === "pending") body = <p className="jc-display italic text-3xl text-[#A8865A]" data-testid="prive-pending">Your verification is in review.</p>;
  else if (s === "suspended" || s === "revoked") body = <p className="text-sm text-[#4A403B]" data-testid="prive-suspended">Your Privé access is currently {s}. Please <Link className="underline" to="/contact">contact us</Link>.</p>;
  else body = <>{s === "rejected" && <p className="text-sm text-[#A8865A] mb-4">We couldn't verify your last request — you're welcome to try again.</p>}<RequestForm onDone={reload} /></>;
  return (
    <div className="bg-[#FBF8F3] p-8 md:p-12 border border-[#E8DCCB] rounded-sm" data-testid="prive-gate">
      <p className="jc-eyebrow inline-flex items-center gap-2 mb-6"><Lock className="w-3.5 h-3.5" strokeWidth={1.25} /> How to join</p>
      <p className="text-sm text-[#4A403B] leading-relaxed" data-testid="prive-requirement">{SET_REQUIREMENT}</p>
      <Link to={PRIVE_SHOP_URL} className="mt-8 inline-block text-[11px] uppercase tracking-[0.22em] border-b border-[#2B2522] pb-1" data-testid="prive-shop-link">Shop qualifying sets</Link>
      <div className="mt-8 pt-8 border-t border-[#E8DCCB]">{body}</div>
    </div>
  );
}

function Dashboard({ name }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");
  useEffect(() => {
    cApi.get("/community/prive/dashboard").then(({ data }) => setD(data)).catch((e) => setErr(errMsg(e)));
  }, []);
  if (err) return <EmptyState title={err} />;
  if (!d) return null;
  return (
    <div data-testid="prive-dashboard">
      <p className="jc-eyebrow">Your private dashboard</p>
      <h2 className="jc-display text-4xl md:text-5xl mt-3">Hello, <em className="italic text-[#A8865A]">{(name || "beautiful").split(" ")[0]}</em>.</h2>
      <div className="mt-14">
        <p className="jc-eyebrow mb-8">Exclusive announcements</p>
        {d.announcements.length ? (
          <div className="grid md:grid-cols-3 gap-10">{d.announcements.map((a) => <BulletinCard key={a.id} item={a} />)}</div>
        ) : <p className="text-sm text-[#7A6A62]" data-testid="prive-no-announcements">New Privé announcements will appear here.</p>}
      </div>
      <div className="mt-20">
        <p className="jc-eyebrow mb-8">Privé-only experiences</p>
        {d.events.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-10">{d.events.map((e, i) => <EventCard key={e.id} event={{ ...e, spots_left: e.capacity - (e.confirmed_count || 0) }} idx={i} />)}</div>
        ) : <p className="text-sm text-[#7A6A62]">Private get-togethers are being planned.</p>}
      </div>
      <div className="mt-20 bg-[#F3E1DC]/70 p-8 md:p-12 rounded-sm flex flex-wrap items-center justify-between gap-6">
        <div>
          <p className="jc-display text-3xl">Claim your complimentary business headshot</p>
          <p className="text-sm text-[#7A6A62] mt-2">Reply to your Privé welcome email or message us to book your session.</p>
        </div>
        <Link to="/contact" className="jc-btn jc-btn-primary" data-testid="prive-headshot-btn">Book my headshot</Link>
      </div>
    </div>
  );
}

export default function Prive() {
  const { me, level } = useCommunityMe();
  return (
    <CommunityLayout>
      <section className="relative overflow-hidden jc-grain">
        <img src={STOCK.prive} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#2B2522]/80 via-[#2B2522]/45 to-transparent" />
        <div className="relative max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-28 md:py-40">
          <p className="jc-eyebrow !text-[#E7C4BC] jc-fade jc-d1">Jenova Privé Club</p>
          <h1 className="jc-display text-[#FBF8F3] text-5xl sm:text-6xl lg:text-7xl mt-5 max-w-3xl uppercase jc-fade jc-d2" data-testid="prive-headline">Welcome to Jenova Privé Club.</h1>
          <p className="mt-7 text-[#FBF8F3]/85 max-w-xl text-base md:text-lg leading-relaxed jc-fade jc-d3">
            An exclusive space for the women who wear Jenova, support our vision, and are part of our growing sisterhood.
          </p>
        </div>
      </section>
      <section className="max-w-[1600px] mx-auto px-6 md:px-12 lg:px-16 py-24">
        {level === "prive" ? <Dashboard name={me?.membership?.name || me?.user?.name} /> : (
          <div className="grid md:grid-cols-12 gap-14">
            <div className="md:col-span-7">
              <p className="jc-eyebrow">Member benefits</p>
              <ul className="mt-8 divide-y divide-[#E8DCCB] border-y border-[#E8DCCB]" data-testid="prive-benefits">
                {BENEFITS.map(([Icon, t]) => (
                  <li key={t} className="flex items-center gap-5 py-5 group">
                    <Icon className="w-5 h-5 text-[#A8865A] group-hover:scale-110 transition-transform duration-500" strokeWidth={1} />
                    <span className="font-serif text-xl md:text-2xl">{t}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-5"><Gate /></div>
          </div>
        )}
      </section>
    </CommunityLayout>
  );
}
